import { computed, inject, Injectable } from '@angular/core';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';

import { Me } from 'src/providers/me/me';
import { Auth } from 'src/providers/auth/auth';

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  list: () => [...subscriptionKeys.all, 'list'] as const,
};

@Injectable({ providedIn: 'root' })
export class SubscriptionsQueriesService {
  private readonly me = inject(Me);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();

  readonly subscriptionsQuery = injectQuery(() => ({
    queryKey: subscriptionKeys.list(),
    queryFn: () => this.me.getSubscriptions(),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: this.auth.authenticated(),
  }));

  readonly subscriptions = computed(() => this.subscriptionsQuery.data() ?? []);

  async getSubscriptions(): Promise<any[]> {
    const cached = this.subscriptionsQuery.data();
    if (cached) {
      return cached;
    }
    const result = await this.subscriptionsQuery.refetch();
    return result.data ?? [];
  }

  /**
   * Always hits the API, ignoring the 10-minute cache. Use before anything that must not act
   * on a stale plan — e.g. deciding whether to show the subscribe page at all. `invalidate()`
   * only marks the cache stale; `getSubscriptions()` still hands back the old list until the
   * background refetch lands.
   */
  async getFreshSubscriptions(): Promise<any[]> {
    const result = await this.subscriptionsQuery.refetch();
    return result.data ?? [];
  }

  invalidate(): void {
    this.queryClient.invalidateQueries({ queryKey: subscriptionKeys.list() });
  }
}
